<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePermissions } from 'dashboard/composables/usePermissions';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BrandIcon from 'dashboard/components/widgets/BrandIcon.vue';
import AddWhatsappBranch from './AddWhatsappBranch.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { isAdminOrSuperAdmin } = usePermissions();

const branches = useMapGetter('branches/getBranches');
function branchPillFor(inbox) {
  if (!inbox?.branch_id) return null;
  const b = (branches.value || []).find(x => x.id === inbox.branch_id);
  return b ? { code: b.code, name: b.name } : null;
}
onMounted(() => {
  store.dispatch('branches/get');
});

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');
const showAddWhatsapp = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');

// 2026-05-19 (Victor): bandejas ocultas de la lista de Ajustes → Inboxes
// (ej. duplicadas/jubiladas sin número real). NO se borran — solo se ocultan
// de esta vista; siguen accesibles por URL directa y conservan sus datos.
const accountId = useMapGetter('getCurrentAccountId');
const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](accountId.value)
);
const hiddenInboxIds = computed(() => {
  const ids = currentAccount.value?.settings?.hidden_settings_inbox_ids;
  return Array.isArray(ids) ? ids.map(Number) : [];
});

const inboxesList = computed(() => {
  const hidden = hiddenInboxIds.value;
  return inboxes.value
    ?.filter(i => !hidden.includes(Number(i.id)))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
});

const activeFilter = ref('all'); // 'all' | 'connected' | 'available'

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  let list = inboxesList.value || [];
  if (query) list = picoSearch(list, query, ['name', 'channel_type']);
  return list;
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

// Catálogo de tipos de canal con su metadata visual y la ruta sub_page
// para crear una NUEVA cuenta directamente (saltea el channel picker).
const CHANNEL_TYPES = [
  { key: 'whatsapp', label: 'WhatsApp', icon: '💬', gradient: 'from-emerald-500 to-green-600', border: 'border-emerald-500/40 hover:border-emerald-500', subPage: 'api', match: t => /Whatsapp|WhatsApp/.test(t) },
  { key: 'instagram', label: 'Instagram', icon: '📷', gradient: 'from-pink-500 via-rose-500 to-amber-500', border: 'border-pink-500/40 hover:border-pink-500', subPage: 'instagram', match: (t, inbox) => /Instagram/.test(t) || inbox?.medium === 'instagram' },
  { key: 'facebook', label: 'Facebook Messenger', icon: 'f', gradient: 'from-blue-500 to-blue-700', border: 'border-blue-500/40 hover:border-blue-500', subPage: 'facebook', match: t => /Facebook/.test(t) && !/Whatsapp/.test(t) },
  { key: 'api', label: 'Canal API', icon: '{ }', gradient: 'from-slate-500 to-slate-700', border: 'border-slate-500/40 hover:border-slate-500', subPage: 'api', match: t => /Api/.test(t) },
  { key: 'email', label: 'Email', icon: '✉️', gradient: 'from-blue-400 to-cyan-600', border: 'border-cyan-500/40 hover:border-cyan-500', subPage: 'email', match: t => /Email/.test(t) },
  { key: 'website', label: 'Web Widget', icon: '💻', gradient: 'from-violet-500 to-purple-700', border: 'border-violet-500/40 hover:border-violet-500', subPage: 'website', match: t => /Web|Channel::Web/.test(t) },
  { key: 'twilio', label: 'SMS Twilio', icon: '📱', gradient: 'from-amber-500 to-orange-600', border: 'border-amber-500/40 hover:border-amber-500', subPage: 'twilio', match: t => /Twilio|Sms/.test(t) },
  { key: 'telegram', label: 'Telegram', icon: '✈️', gradient: 'from-cyan-400 to-blue-600', border: 'border-cyan-500/40 hover:border-cyan-500', subPage: 'telegram', match: t => /Telegram/.test(t) },
  { key: 'line', label: 'Line', icon: '💚', gradient: 'from-green-400 to-emerald-600', border: 'border-green-500/40 hover:border-green-500', subPage: 'line', match: t => /Line/.test(t) },
];

function channelMeta(inbox) {
  const t = inbox.channel_type || '';
  return CHANNEL_TYPES.find(c => c.match(t, inbox)) || {
    label: 'Canal', icon: '🔌', gradient: 'from-slate-500 to-slate-700', border: 'border-slate-500/40 hover:border-slate-500', subPage: 'api',
  };
}

// Agrupa los inboxes filtrados por tipo de canal.
const groupedConnected = computed(() => {
  const map = new Map();
  for (const inbox of filteredInboxesList.value || []) {
    const meta = channelMeta(inbox);
    if (!map.has(meta.key)) map.set(meta.key, { meta, items: [] });
    map.get(meta.key).items.push(inbox);
  }
  return Array.from(map.values());
});

// P1 · 2026-05-16 (Victor): tarjetas por canal + expandir cuentas al click.
const expandedChannel = ref(null);
function toggleChannel(key) {
  expandedChannel.value = expandedChannel.value === key ? null : key;
}

// Devuelve los tipos de canal que NO tienen ninguna cuenta conectada (para mostrar disponibles).
const disconnectedChannelTypes = computed(() => {
  const connectedKeys = new Set((inboxesList.value || []).map(i => channelMeta(i).key));
  return CHANNEL_TYPES.filter(c => !connectedKeys.has(c.key));
});

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #body>
      <!-- M1.B · Glass Negro Premium · lista horizontal -->
      <div class="m1-bg">
        <!-- Header propio (reemplaza el banner) -->
        <div class="m1-head">
          <div>
            <h1 class="m1-h1">Inboxes</h1>
            <p class="m1-sub">{{ inboxesList?.length || 0 }} canales conectados · WhatsApp, Instagram, Facebook</p>
          </div>
          <div class="m1-actions">
            <span class="m1-count">{{ inboxesList?.length || 0 }} inboxes</span>
            <button
              v-if="isAdminOrSuperAdmin"
              type="button"
              class="m1-btn-wa"
              @click="showAddWhatsapp = true"
            >💬 + Nuevo WhatsApp</button>
            <router-link v-if="isAdminOrSuperAdmin" :to="{ name: 'settings_inbox_new' }">
              <button class="m1-btn-secondary">+ Otro canal</button>
            </router-link>
          </div>
        </div>

        <!-- Search M1 -->
        <div class="m1-search">
          <input v-model="searchQuery" type="text" placeholder="Buscar inbox por nombre…" />
        </div>

        <!-- Tabs M1 -->
        <div class="m1-tabs">
          <button
            type="button"
            class="m1-tab"
            :class="{ active: activeFilter === 'all' }"
            @click="activeFilter = 'all'"
          >Todas <span class="m1-tab-badge">{{ inboxesList?.length || 0 }}</span></button>
          <button
            type="button"
            class="m1-tab"
            :class="{ active: activeFilter === 'connected' }"
            @click="activeFilter = 'connected'"
          >✓ Conectadas <span class="m1-tab-badge">{{ inboxesList?.length || 0 }}</span></button>
          <button
            type="button"
            class="m1-tab"
            :class="{ active: activeFilter === 'available' }"
            @click="activeFilter = 'available'"
          >Disponibles <span class="m1-tab-badge">{{ disconnectedChannelTypes.length }}</span></button>
        </div>

        <!-- Conectadas · P1: tarjetas por canal + expandir cuentas (2026-05-16 Victor) -->
        <template v-if="activeFilter !== 'available'">
          <div v-if="!filteredInboxesList?.length && searchQuery" class="m1-empty">
            {{ $t('INBOX_MGMT.NO_RESULTS') }}
          </div>

          <div v-if="groupedConnected.length" class="m1-available-grid">
            <button
              v-for="group in groupedConnected"
              :key="group.meta.key"
              type="button"
              class="m1-available-card m1-conn-card"
              :class="{ 'm1-conn-open': expandedChannel === group.meta.key }"
              @click="toggleChannel(group.meta.key)"
            >
              <span class="m1-conn-badge">{{ group.items.length }} conectada{{ group.items.length === 1 ? '' : 's' }}</span>
              <div class="m1-section-icon" :class="`m1-icon-${group.meta.key}`">
                <span v-if="group.meta.key === 'whatsapp' || group.meta.key === 'api'">{ }</span>
                <span v-else-if="group.meta.key === 'facebook'">f</span>
                <span v-else-if="group.meta.key === 'instagram'">📷</span>
                <BrandIcon v-else :channel="group.meta.key" :size="36" />
              </div>
              <div class="m1-available-name">{{ group.meta.label }}</div>
              <div class="m1-available-sub">● Conectado</div>
              <div class="m1-conn-cta">{{ expandedChannel === group.meta.key ? 'Ocultar ▴' : 'Ver cuentas ▾' }}</div>
            </button>
          </div>

          <!-- Panel expandido: cuentas del canal seleccionado -->
          <template v-for="group in groupedConnected" :key="`exp-${group.meta.key}`">
            <div v-if="expandedChannel === group.meta.key" class="m1-section">
            <div class="m1-section-head">
              <div class="m1-section-left">
                <div class="m1-section-icon" :class="`m1-icon-${group.meta.key}`">
                  <span v-if="group.meta.key === 'whatsapp' || group.meta.key === 'api'">{ }</span>
                  <span v-else-if="group.meta.key === 'facebook'">f</span>
                  <span v-else-if="group.meta.key === 'instagram'">📷</span>
                  <BrandIcon v-else :channel="group.meta.key" :size="20" />
                </div>
                <div class="m1-section-title">{{ group.meta.label }}</div>
                <span class="m1-section-count">{{ group.items.length }} {{ group.items.length === 1 ? 'cuenta' : 'cuentas' }}</span>
              </div>
              <router-link
                v-if="isAdminOrSuperAdmin"
                :to="{ name: 'settings_inboxes_page_channel', params: { sub_page: group.meta.subPage } }"
                class="m1-section-add"
              >+ Agregar otra cuenta</router-link>
            </div>

            <div class="m1-list">
              <div class="m1-list-head">
                <div></div>
                <div>Nombre</div>
                <div>Sucursal</div>
                <div>Estado</div>
                <div class="m1-cell-right">Acciones</div>
              </div>
              <div v-for="inbox in group.items" :key="inbox.id" class="m1-row">
                <div class="m1-av" :class="`m1-av-${group.meta.key}`">
                  <img v-if="inbox.avatar_url" :src="inbox.avatar_url" :alt="inbox.name" />
                  <span v-else-if="group.meta.key === 'api' || group.meta.key === 'whatsapp'">&lt;/&gt;</span>
                  <span v-else-if="group.meta.key === 'facebook'">{{ (inbox.name || 'F').charAt(0).toUpperCase() }}</span>
                  <span v-else-if="group.meta.key === 'instagram'">📷</span>
                  <BrandIcon v-else :channel="group.meta.key" :size="22" />
                </div>
                <div class="m1-info">
                  <div class="m1-name">{{ inbox.name }}</div>
                  <div class="m1-type">{{ group.meta.label }}</div>
                </div>
                <div class="m1-branch">
                  <span v-if="branchPillFor(inbox)" class="m1-branch-pill">{{ branchPillFor(inbox).code }}</span>
                  <span v-if="branchPillFor(inbox)" class="m1-branch-name">{{ branchPillFor(inbox).name }}</span>
                  <span v-else class="m1-branch-empty">— Sin asignar —</span>
                </div>
                <div><span class="m1-status">Activo</span></div>
                <div class="m1-row-actions">
                  <router-link :to="{ name: 'settings_inbox_show', params: { inboxId: inbox.id } }">
                    <button type="button" class="m1-btn-row">⚙ Configurar</button>
                  </router-link>
                  <button v-if="isAdminOrSuperAdmin" type="button" class="m1-btn-row m1-btn-danger" @click="openDelete(inbox)">🗑</button>
                </div>
              </div>
              <router-link
                v-if="isAdminOrSuperAdmin"
                :to="{ name: 'settings_inboxes_page_channel', params: { sub_page: group.meta.subPage } }"
                class="m1-add-row"
              >
                <div class="m1-add-plus">+</div>
                <div class="m1-add-lbl">Agregar otra cuenta de {{ group.meta.label }}</div>
              </router-link>
            </div>
            </div>
          </template>
        </template>

        <!-- Tab Disponibles -->
        <div v-if="activeFilter === 'available' && disconnectedChannelTypes.length > 0" class="m1-section">
          <div class="m1-section-head">
            <div class="m1-section-left">
              <div class="m1-section-icon">⚡</div>
              <div class="m1-section-title">Disponibles para conectar</div>
              <span class="m1-section-count">{{ disconnectedChannelTypes.length }} canales</span>
            </div>
          </div>
          <div class="m1-available-grid">
            <router-link
              v-for="ch in disconnectedChannelTypes"
              :key="ch.key"
              :to="{ name: 'settings_inboxes_page_channel', params: { sub_page: ch.subPage } }"
              class="m1-available-card"
            >
              <BrandIcon :channel="ch.key" :size="36" />
              <div class="m1-available-name">{{ ch.label }}</div>
              <div class="m1-available-sub">No conectado</div>
              <button type="button" class="m1-btn-row" style="margin-top:8px;width:100%;">+ Conectar</button>
            </router-link>
          </div>
        </div>
      </div>

    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />

    <AddWhatsappBranch
      v-if="showAddWhatsapp"
      @close="showAddWhatsapp = false"
      @connected="showAddWhatsapp = false"
    />
  </SettingsLayout>
</template>

<style scoped>
/* M1.B · Glass Negro Premium · pixel-perfect del mockup inboxes-m1-densidad-B-rows.html */
.m1-bg {
  background: radial-gradient(ellipse 80% 50% at 50% 0%, rgba(251,191,36,0.06), transparent 70%), #050505;
  color: #fff;
  min-height: 100%;
  padding: 28px 36px;
  margin: -1.5rem;
  font-family: -apple-system, system-ui, sans-serif;
}
/* Header */
.m1-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.m1-h1 { font-size: 26px; font-weight: 800; letter-spacing: -0.5px; color: #fff; }
.m1-sub { color: rgba(255,255,255,0.4); font-size: 12px; margin-top: 2px; }
.m1-actions { display: flex; gap: 8px; align-items: center; }
.m1-count {
  font-size: 11px; padding: 8px 14px;
  color: #fbbf24;
  background: rgba(251,191,36,0.08);
  border: 1px solid rgba(251,191,36,0.25);
  border-radius: 9px; font-weight: 700;
}
.m1-btn-wa {
  padding: 10px 18px; border-radius: 10px;
  font-size: 12px; font-weight: 800; color: #000; cursor: pointer; border: none;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  box-shadow: 0 0 16px rgba(34,197,94,0.4);
  transition: transform 0.15s, box-shadow 0.15s;
}
.m1-btn-wa:hover { transform: translateY(-1px); box-shadow: 0 0 24px rgba(34,197,94,0.55); }
.m1-btn-secondary {
  padding: 10px 16px; border-radius: 10px;
  font-size: 12px; font-weight: 700; color: #fff; cursor: pointer;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
}
.m1-btn-secondary:hover { background: rgba(255,255,255,0.10); border-color: rgba(255,255,255,0.20); }

/* Search */
.m1-search { position: relative; max-width: 380px; margin-bottom: 18px; }
.m1-search input {
  width: 100%; padding: 10px 14px 10px 36px; font-size: 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 10px; color: #fff; outline: none;
  transition: border-color 0.15s;
}
.m1-search input:focus { border-color: rgba(251,191,36,0.5); }
.m1-search::before { content: '🔍'; position: absolute; left: 12px; top: 10px; font-size: 11px; opacity: 0.5; }

/* Tabs */
.m1-tabs { display: flex; gap: 4px; margin-bottom: 22px; }
.m1-tab {
  padding: 7px 13px; border-radius: 8px;
  font-size: 11px; font-weight: 700;
  color: rgba(255,255,255,0.55); cursor: pointer;
  background: transparent; border: 1px solid transparent;
  transition: all 0.15s;
}
.m1-tab:hover:not(.active) { background: rgba(255,255,255,0.04); }
.m1-tab.active {
  color: #fbbf24;
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.30);
}
.m1-tab-badge {
  display: inline-block; margin-left: 4px; padding: 0 6px;
  background: rgba(255,255,255,0.10); border-radius: 4px;
  font-size: 9px; font-weight: 800;
}
.m1-tab.active .m1-tab-badge { background: rgba(251,191,36,0.20); color: #fbbf24; }

/* Section */
.m1-section { margin-bottom: 24px; }
.m1-section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; padding: 0 4px; }
.m1-section-left { display: flex; align-items: center; gap: 9px; }
.m1-section-icon {
  width: 26px; height: 26px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 11px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.10);
  color: #fbbf24;
}
.m1-section-icon.m1-icon-facebook { color: #1877f2; background: rgba(24,119,242,0.10); border-color: rgba(24,119,242,0.30); }
.m1-section-icon.m1-icon-instagram { color: #ec4899; background: linear-gradient(135deg, rgba(245,133,41,0.15), rgba(221,42,123,0.15)); border-color: rgba(221,42,123,0.30); }
.m1-section-title { font-size: 13px; font-weight: 800; color: #fff; }
.m1-section-count {
  font-size: 10px; color: rgba(255,255,255,0.40);
  padding: 2px 6px; background: rgba(255,255,255,0.04);
  border-radius: 4px;
}
.m1-section-add {
  font-size: 11px; color: #fbbf24; cursor: pointer; font-weight: 700;
  text-decoration: none;
}
.m1-section-add:hover { color: #fcd34d; }

/* List container (cristal negro + facetas) */
.m1-list {
  position: relative;
  background: rgba(10,10,10,0.88);
  border: 1.5px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(255,255,255,0.04) inset, 0 14px 36px rgba(0,0,0,0.55);
}
.m1-list::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  border-radius: 14px;
  background:
    linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.06) 22%, transparent 24%),
    radial-gradient(circle at 100% 0%, rgba(255,255,255,0.08), transparent 60%);
  z-index: 1;
}
.m1-list > * { position: relative; z-index: 2; }

/* Header de tabla */
.m1-list-head {
  display: grid; grid-template-columns: 44px 1fr 220px 100px 200px;
  gap: 14px; align-items: center;
  padding: 10px 16px;
  font-size: 10px; color: rgba(255,255,255,0.4);
  text-transform: uppercase; letter-spacing: 0.8px; font-weight: 700;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.m1-cell-right { text-align: right; }

/* Filas */
.m1-row {
  display: grid; grid-template-columns: 44px 1fr 220px 100px 200px;
  gap: 14px; align-items: center;
  padding: 11px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  cursor: pointer; transition: background 0.15s;
}
.m1-row:last-child { border-bottom: none; }
.m1-row:hover { background: rgba(251,191,36,0.04); }

/* Avatar */
.m1-av {
  width: 36px; height: 36px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 13px; color: #fff;
  box-shadow: 0 0 0 1px rgba(255,255,255,0.10) inset;
  overflow: hidden; flex-shrink: 0;
}
.m1-av img { width: 100%; height: 100%; object-fit: cover; }
.m1-av-api,
.m1-av-whatsapp {
  background: linear-gradient(135deg, #475569, #334155);
  color: #cbd5e1; font-family: monospace; font-size: 16px;
}
.m1-av-facebook {
  background: linear-gradient(135deg, #1877f2, #0c4eb8);
  font-family: Georgia, serif; font-style: italic; font-size: 18px;
}
.m1-av-instagram { background: linear-gradient(135deg, #f58529, #dd2a7b 50%, #8134af); }
.m1-av-email { background: linear-gradient(135deg, #06b6d4, #0e7490); }
.m1-av-twilio { background: linear-gradient(135deg, #f59e0b, #d97706); }
.m1-av-telegram { background: linear-gradient(135deg, #22d3ee, #0ea5e9); }
.m1-av-line { background: linear-gradient(135deg, #22c55e, #16a34a); }
.m1-av-website { background: linear-gradient(135deg, #a855f7, #6d28d9); }

/* Info */
.m1-info { min-width: 0; }
.m1-name {
  font-size: 13px; font-weight: 800; line-height: 1.2;
  color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.m1-type {
  font-size: 10px; color: rgba(255,255,255,0.45);
  text-transform: uppercase; letter-spacing: 0.5px;
  margin-top: 2px; font-weight: 600;
}

/* Sucursal */
.m1-branch { font-size: 11px; color: rgba(255,255,255,0.7); display: flex; align-items: center; gap: 6px; }
.m1-branch-pill {
  font-size: 10px; font-weight: 800;
  padding: 2px 7px; border-radius: 5px;
  background: rgba(251,191,36,0.12);
  color: #fbbf24;
  border: 1px solid rgba(251,191,36,0.30);
  letter-spacing: 0.6px;
  flex-shrink: 0;
}
.m1-branch-name { color: rgba(255,255,255,0.7); }
.m1-branch-empty { color: rgba(255,255,255,0.40); font-style: italic; }

/* Status pill */
.m1-status {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 9px; border-radius: 999px;
  font-size: 9px; font-weight: 800; letter-spacing: 1px;
  text-transform: uppercase;
  background: rgba(34,197,94,0.15); color: #22c55e;
  border: 1px solid rgba(34,197,94,0.40);
  width: fit-content;
}
.m1-status::before {
  content: ''; width: 5px; height: 5px; border-radius: 50%;
  background: #22c55e; box-shadow: 0 0 6px #22c55e;
  animation: m1-pulse 1.6s ease-in-out infinite;
}
@keyframes m1-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }

/* Acciones de fila */
.m1-row-actions { display: flex; gap: 6px; justify-content: flex-end; }
.m1-row-actions a { display: flex; }
.m1-btn-row {
  padding: 6px 12px; border-radius: 7px;
  font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.85);
  cursor: pointer;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  transition: all 0.15s;
}
.m1-btn-row:hover {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.30);
  color: #fbbf24;
}
.m1-btn-danger { padding: 6px 9px; }
.m1-btn-danger:hover {
  background: rgba(239,68,68,0.12);
  border-color: rgba(239,68,68,0.35);
  color: #ef4444;
}

/* Add row */
.m1-add-row {
  padding: 11px 16px;
  border-top: 1px dashed rgba(255,255,255,0.15);
  display: flex; gap: 10px; align-items: center;
  cursor: pointer; transition: background 0.2s;
  text-decoration: none;
}
.m1-add-row:hover { background: rgba(251,191,36,0.04); }
.m1-add-plus {
  width: 36px; height: 36px; border-radius: 9px;
  background: rgba(255,255,255,0.05);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; font-weight: 200;
  color: rgba(255,255,255,0.4);
  transition: all 0.2s;
}
.m1-add-row:hover .m1-add-plus { color: #fbbf24; background: rgba(251,191,36,0.10); }
.m1-add-lbl { font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.6); }
.m1-add-row:hover .m1-add-lbl { color: #fbbf24; }

/* Empty state */
.m1-empty {
  text-align: center; padding: 32px 16px;
  color: rgba(255,255,255,0.45); font-size: 12px;
}

/* Tab Disponibles · grid de canales */
.m1-available-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}
.m1-available-card {
  position: relative;
  padding: 16px; border-radius: 13px;
  background: rgba(10,10,10,0.88);
  border: 1.5px solid rgba(255,255,255,0.10);
  box-shadow: 0 0 0 1px rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.4);
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  cursor: pointer; transition: all 0.15s;
  text-decoration: none;
  text-align: center;
}
.m1-available-card::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  border-radius: 13px;
  background:
    linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.06) 22%, transparent 24%),
    radial-gradient(circle at 100% 0%, rgba(255,255,255,0.08), transparent 60%);
  z-index: 1;
}
.m1-available-card > * { position: relative; z-index: 2; }
.m1-available-card:hover {
  transform: translateY(-3px);
  border-color: rgba(251,191,36,0.30);
  box-shadow: 0 0 0 1px rgba(251,191,36,0.15) inset, 0 14px 28px rgba(251,191,36,0.10), 0 14px 30px rgba(0,0,0,0.6);
}
.m1-available-name { font-size: 12px; font-weight: 800; color: #fff; }
.m1-available-sub { font-size: 10px; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
/* P1 · tarjetas de canal conectado (2026-05-16 Victor) · layout izquierda
   como el mockup: ícono arriba-izquierda, badge arriba-derecha (sin solape) */
.m1-conn-card { cursor: pointer; border: 1px solid rgba(255,255,255,0.10); background: none; font-family: inherit; align-items: flex-start; text-align: left; padding-top: 18px; }
.m1-conn-card .m1-section-icon { margin-bottom: 6px; }
.m1-conn-card .m1-available-sub { color: #34d399; }
.m1-conn-badge { position: absolute; top: 14px; right: 14px; z-index: 3; background: rgba(52,211,153,0.14); color: #34d399; border: 1px solid rgba(52,211,153,0.35); font-size: 10px; font-weight: 800; padding: 3px 9px; border-radius: 999px; white-space: nowrap; }
.m1-conn-cta { margin-top: 10px; font-size: 11px; font-weight: 700; color: #b8924a; }
.m1-conn-card.m1-conn-open { border-color: rgba(184,146,74,0.55); box-shadow: 0 0 0 1px rgba(184,146,74,0.25); }
</style>
